import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthProvider";
import { 
  addAdminBlockedDate, 
  removeAdminBlockedDate, 
  getBlockedDatesInRange,
  BlockedDate 
} from "../../services/subscription-advanced.service";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { localDateKey } from "../../services/subscription.service";
import { Kitchen } from "../../types";
import { toast } from "react-hot-toast";

interface BlockedDatesSectionProps {
  trigger?: number;
  onAdminEvent?: () => void;
}

export const BlockedDatesSection: React.FC<BlockedDatesSectionProps> = ({ 
  trigger, 
  onAdminEvent 
}) => {
  const { user } = useAuth();
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  const [kitchens, setKitchens] = useState<Kitchen[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  
  // Form state
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [dateRangeStart, setDateRangeStart] = useState("");
  const [dateRangeEnd, setDateRangeEnd] = useState("");
  const [reason, setReason] = useState("");
  const [blockType, setBlockType] = useState<"platform" | "kitchen">("platform");
  const [selectedKitchenId, setSelectedKitchenId] = useState("");
  const [selectionMode, setSelectionMode] = useState<"single" | "range" | "bulk">("single");

  useEffect(() => {
    loadData();
  }, [trigger]);

  const loadData = async () => {
    setLoading(true);
    try {
      const today = new Date();
      const futureDate = new Date();
      futureDate.setDate(today.getDate() + 90);
      
      const [blocked, kitchenSnapshot] = await Promise.all([
        getBlockedDatesInRange(today, futureDate),
        getDocs(collection(db, "kitchens"))
      ]);
      
      setBlockedDates(blocked.sort((a, b) => a.date.localeCompare(b.date)));
      setKitchens(kitchenSnapshot.docs.map(doc => ({ 
        uid: doc.id, 
        ...doc.data() 
      } as Kitchen)));
    } catch (error) {
      console.error("Error loading blocked dates:", error);
      toast.error("Failed to load blocked dates");
    } finally {
      setLoading(false);
    }
  };

  // Generate dates in range
  const generateDateRange = (start: string, end: string): string[] => {
    const dates: string[] = [];
    const current = new Date(start + "T00:00:00");
    const endDate = new Date(end + "T00:00:00");
    
    while (current <= endDate) {
      const dateStr = localDateKey(current);
      dates.push(dateStr);
      current.setDate(current.getDate() + 1);
    }
    
    return dates;
  };

  // Handle date range selection
  const handleDateRangeChange = () => {
    if (!dateRangeStart || !dateRangeEnd) {
      toast.error("Please select both start and end dates");
      return;
    }
    
    if (dateRangeStart > dateRangeEnd) {
      toast.error("Start date must be before end date");
      return;
    }
    
    const generated = generateDateRange(dateRangeStart, dateRangeEnd);
    setSelectedDates(generated);
    toast.success(`Selected ${generated.length} dates`);
  };

  // Toggle individual date selection
  const toggleDate = (dateStr: string) => {
    setSelectedDates(prev =>
      prev.includes(dateStr) 
        ? prev.filter(d => d !== dateStr)
        : [...prev, dateStr].sort()
    );
  };

  // Handle adding multiple blocked dates
  const handleAddBlockedDates = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedDates.length === 0) {
      toast.error("Please select at least one date");
      return;
    }
    
    if (!reason.trim()) {
      toast.error("Please provide a reason");
      return;
    }
    
    if (blockType === "kitchen" && !selectedKitchenId) {
      toast.error("Please select a kitchen");
      return;
    }
    
    setAdding(true);
    try {
      // Add all selected dates in parallel
      const promises = selectedDates.map(dateStr => {
        const date = new Date(dateStr + "T00:00:00");
        return addAdminBlockedDate(
          date,
          reason.trim(),
          user!.uid,
          blockType,
          blockType === "kitchen" ? selectedKitchenId : undefined
        );
      });
      
      await Promise.all(promises);
      toast.success(`${selectedDates.length} date(s) blocked successfully`);
      
      // Reset form
      setSelectedDates([]);
      setDateRangeStart("");
      setDateRangeEnd("");
      setReason("");
      setBlockType("platform");
      setSelectedKitchenId("");
      setSelectionMode("single");
      
      loadData();
      onAdminEvent?.();
    } catch (error) {
      console.error("Error blocking dates:", error);
      toast.error("Failed to block dates");
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveBlockedDate = async (blockedDateId: string) => {
    if (!window.confirm("Remove this blocked date? Tiffins may be restored to scheduled status.")) {
      return;
    }
    
    try {
      await removeAdminBlockedDate(blockedDateId);
      toast.success("Blocked date removed");
      loadData();
      onAdminEvent?.();
    } catch (error) {
      console.error("Error removing blocked date:", error);
      toast.error("Failed to remove blocked date");
    }
  };

  const getKitchenName = (kitchenId?: string) => {
    if (!kitchenId) return "All Kitchens";
    const kitchen = kitchens.find(k => k.uid === kitchenId);
    return kitchen?.kitchen_name || "Unknown Kitchen";
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("en-IN", { 
      weekday: "short", 
      year: "numeric", 
      month: "short", 
      day: "numeric" 
    });
  };

  const getTodayString = () => {
    const today = new Date();
    return localDateKey(today);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-bold text-gray-900">📅 Blocked Dates Management</h3>
        <button
          onClick={loadData}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Add Blocked Dates Form */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
        <h4 className="text-lg font-bold text-gray-900 mb-4">🚫 Block Dates</h4>
        
        {/* Selection Mode Toggle */}
        <div className="grid grid-cols-3 gap-2 mb-6">
          {[
            { mode: "single" as const, label: "📍 Single", desc: "One date" },
            { mode: "range" as const, label: "📊 Range", desc: "Start to End" },
            { mode: "bulk" as const, label: "✓ Bulk", desc: "Pick multiple" }
          ].map(({ mode, label, desc }) => (
            <button
              key={mode}
              onClick={() => {
                setSelectionMode(mode);
                setSelectedDates([]);
                setDateRangeStart("");
                setDateRangeEnd("");
              }}
              className={`p-3 rounded-lg border transition-all ${
                selectionMode === mode
                  ? "border-blue-600 bg-blue-100 text-blue-900 font-bold"
                  : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              <div className="text-sm font-bold">{label}</div>
              <div className="text-xs text-gray-500">{desc}</div>
            </button>
          ))}
        </div>

        <form onSubmit={handleAddBlockedDates} className="space-y-4">
          {/* Date Selection Section */}
          <div className="bg-white rounded-lg p-4 border border-blue-200">
            <label className="block text-sm font-bold text-gray-700 mb-3">
              Select Dates <span className="text-red-500">*</span>
            </label>

            {selectionMode === "single" && (
              <input
                type="date"
                value={selectedDates[0] || ""}
                onChange={(e) => setSelectedDates(e.target.value ? [e.target.value] : [])}
                min={getTodayString()}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            )}

            {selectionMode === "range" && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">From</label>
                    <input
                      type="date"
                      value={dateRangeStart}
                      onChange={(e) => setDateRangeStart(e.target.value)}
                      min={getTodayString()}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">To</label>
                    <input
                      type="date"
                      value={dateRangeEnd}
                      onChange={(e) => setDateRangeEnd(e.target.value)}
                      min={dateRangeStart || getTodayString()}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleDateRangeChange}
                  className="w-full px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  📅 Generate Range
                </button>
              </div>
            )}

            {selectionMode === "bulk" && (
              <div className="grid grid-cols-7 gap-2">
                {Array.from({ length: 30 }, (_, i) => {
                  const date = new Date();
                  date.setDate(date.getDate() + i);
                  const dateStr = localDateKey(date);
                  const isSelected = selectedDates.includes(dateStr);
                  const day = date.getDate();
                  
                  return (
                    <button
                      key={dateStr}
                      type="button"
                      onClick={() => toggleDate(dateStr)}
                      className={`aspect-square flex items-center justify-center rounded-lg text-sm font-bold transition-all ${
                        isSelected
                          ? "bg-blue-600 text-white shadow-md ring-2 ring-blue-400"
                          : "bg-white border border-gray-300 text-gray-700 hover:bg-blue-50"
                      }`}
                      title={formatDate(dateStr)}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            )}

            {selectedDates.length > 0 && (
              <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-sm font-bold text-blue-900 mb-2">
                  Selected: {selectedDates.length} date(s)
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedDates.slice(0, 5).map(d => (
                    <span key={d} className="text-xs bg-blue-200 text-blue-900 px-2 py-1 rounded">
                      {formatDate(d)}
                    </span>
                  ))}
                  {selectedDates.length > 5 && (
                    <span className="text-xs bg-blue-200 text-blue-900 px-2 py-1 rounded">
                      +{selectedDates.length - 5} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Block Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Block Type <span className="text-red-500">*</span>
              </label>
              <select
                value={blockType}
                onChange={(e) => setBlockType(e.target.value as "platform" | "kitchen")}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="platform">🌍 Platform-wide (All Kitchens)</option>
                <option value="kitchen">🏠 Specific Kitchen Only</option>
              </select>
            </div>

            {/* Kitchen Selector (conditional) */}
            {blockType === "kitchen" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Kitchen <span className="text-red-500">*</span>
                </label>
                                <select
                  value={selectedKitchenId}
                  onChange={(e) => setSelectedKitchenId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">-- Choose a Kitchen --</option>
                  {kitchens.map(kitchen => (
                    <option key={kitchen.uid} value={kitchen.uid}>
                      {kitchen.kitchen_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., National Holiday, Maintenance Day, Festival"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={adding || selectedDates.length === 0}
            className="w-full md:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold rounded-lg transition-colors"
          >
            {adding ? "⏳ Blocking..." : `🚫 Block ${selectedDates.length} Date(s)`}
          </button>
        </form>
      </div>

      {/* Blocked Dates List */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h4 className="text-lg font-bold text-gray-900">
            📋 Upcoming Blocked Dates ({blockedDates.length})
          </h4>
        </div>
        
        {blockedDates.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            <div className="text-4xl mb-2">📅</div>
            <p className="font-medium">No blocked dates scheduled</p>
            <p className="text-sm mt-1">All days are currently available for subscriptions</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {blockedDates.map((blocked) => (
              <div
                key={blocked.id}
                className="px-6 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-lg font-bold text-gray-900">
                        {formatDate(blocked.date)}
                      </span>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-bold ${
                          blocked.type === "platform"
                            ? "bg-red-100 text-red-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {blocked.type === "platform" ? "🌍 Platform-wide" : "🏠 Kitchen-specific"}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-1">
                      <span className="font-medium">Reason:</span> {blocked.reason}
                    </p>
                    
                    <p className="text-xs text-gray-500">
                      <span className="font-medium">Applies to:</span> {getKitchenName(blocked.kitchenId)}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoveBlockedDate(blocked.id!)}
                    className="ml-4 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors text-sm font-medium"
                  >
                    🗑️ Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h5 className="font-bold text-blue-900 mb-2">ℹ️ How Multi-Date Blocking Works</h5>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li><strong>Single:</strong> Block one specific date</li>
          <li><strong>Range:</strong> Automatically generate dates between start and end (inclusive)</li>
          <li><strong>Bulk:</strong> Click on a calendar to pick multiple non-consecutive dates</li>
          <li><strong>Platform-wide blocks</strong> affect all kitchens on those dates</li>
          <li><strong>Kitchen-specific blocks</strong> only affect the selected kitchen</li>
          <li>All scheduled tiffins on blocked dates are automatically skipped</li>
          <li>Customers will see these dates as unavailable when creating subscriptions</li>
        </ul>
      </div>
    </div>
  );
};

